import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isSelected } from 'helpers';
import { useDebounce } from 'hooks';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMetaData, DataTablePageEvent, DataTableSortMeta, DataTableStateEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { AvgTransportSpeedUI } from 'types/interfaces';
import { clearFilter } from 'utils';
import VisibilityWrapper from 'utils/wrappers/VisibilityWrapper';

import { AvgTransportSpeedService } from 'components/services/AvgTransportSpeedService';
import { AvgTransportSpeedTemplate } from 'constants/AvgTransportSpeedTemplate';
import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import pageSize from 'ui/shared/paginator/pageSizeValues';
import TaskAvgTransportSpeedCard from 'ui/TaskAvgTransportSpeedCard';




export default function AvgTransportSpeed() {

  const [selectedSpeed, setSelectedSpeed] = useState<AvgTransportSpeedUI>(AvgTransportSpeedTemplate);
  const [speeds, setSpeeds] = useState<AvgTransportSpeedUI[]>([])
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const isSelectedIdExist = isSelected(selectedSpeed);

  const toast = useRef<Toast>(null);


  const AvgTransportSpeedRef = useRef<null | { handleCancel: () => void }>(null);

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const [filters, setFilters] = useState<Record<string, DataTableFilterMetaData>>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    value: { value: null, matchMode: FilterMatchMode.CONTAINS },
    roadCurvature: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  
  const nameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const valueFilterRef = useRef<HTMLInputElement>(null);
  const roadCurvatureFilterRef = useRef<HTMLInputElement>(null)

  const tableElement = document.querySelector('.p-datatable-thead');

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const loadData = useCallback(() => {
    const sort = multiSortMeta?.[0]; 
    const payload = JSON.stringify([
      { field: 'name', value: filters.name?.value, operator: filters.name?.matchMode },
      { field: 'description', value: filters.description?.value, operator: filters.description?.matchMode },
      { field: 'value', value: filters.value?.value, operator: filters.value?.matchMode }, 
      { field: 'roadCurvature', value: filters.roadCurvature?.value, operator: filters.roadCurvature?.matchMode }, 
      { field: 'global', value: filters.global?.value, operator: filters.global?.matchMode }
    ]);

    const service = AvgTransportSpeedService.getAvgTransportSpeed({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    });

    service.then(res => {
      if (res.data.isSuccess) {
        setSpeeds(res.data.value.itemsList);
        setTotalCount(res.data.value.total || 0);
      }
    });
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    debouncedLoadData();
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setMultiSortMeta(e.multiSortMeta ?? null);
  }, []);


  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );
  
  const handleInputChange = (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedOnFilterChange(filter, e);
  };

  const addButtonHandle = () => {
    setSelectedSpeed(AvgTransportSpeedTemplate);
    setDialogVisible(true);
  };  
  


  const renderHeader = () => {
    return (
      <div id="table-header" className="flex justify-content-between">
        <SearchInputWithClear
          onChange={(value) => {
            const mockEvent = {
              target: { value }
            } as React.ChangeEvent<HTMLInputElement>;
            handleInputChange('global')(mockEvent);
          }}
          placeholder="Поиск по ключевым словам"
        />
        <span className="flex align-items-center gap-2">
          <Button
            className="avgTransportSpeeds-add-btn__roles"
            type="button"
            icon="pi pi-plus"
            severity="success"
            rounded
            onClick={() => {
              addButtonHandle();
            }}
          />
          <Button
            className="avgTransportSpeeds-edit-btn__roless"
            type="button"
            icon="pi pi-pencil"
            rounded
            onClick={() => {
              if (toast.current && !isSelectedIdExist) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Выберите среднюю скорость для редактирования',
                  life: 3000
                });
              }
              if (isSelectedIdExist) setDialogVisible(true);
            }}
          />
          <Button
            className="avgTransportSpeeds-delete-btn__roles"
            type="button"
            icon="pi pi-trash"
            severity="danger"
            rounded
            onClick={() => {
              if (selectedSpeed?.id) {
                setDeleteDialogVisible(true);
              }
              if (toast.current && !selectedSpeed?.id) {
                toast.current.show({
                  severity: 'warn',
                  summary: 'Ошибка',
                  detail: 'Выберите среднюю скорость для удаления',
                  life: 3000
                });
              }
            }}
          />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  const onHideDetailAvgTransportSpeed = () => {
    if (AvgTransportSpeedRef.current !== null) {
      AvgTransportSpeedRef.current.handleCancel();
    }
  };

  const handleUpdate = (id: number, AvgTransportSpeed: AvgTransportSpeedUI) =>
    AvgTransportSpeedService.updateAvgTransportSpeed(id, AvgTransportSpeed).then(res => {
      loadData();
      return res;
    });

  const handleAdd = (AvgTransportSpeed: AvgTransportSpeedUI) =>
    AvgTransportSpeedService.createAvgTransportSpeed(AvgTransportSpeed).then(res => {
      loadData();
      return res;
    });

  const closeDialog = () => {
    setDialogVisible(false);
  };

  return (
    <div className="measure-avgTransportSpeeds-table wrapper wrapper--main">
      <Toast ref={toast} />
 
      <Dialog
        header="Настройка параметров средней скорости"
        visible={dialogVisible}
        style={{ 
          minWidth: '630px', 
          // width: '630px',
          // height: '500px',
          minHeight: 380 
        }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        maximizable
        resizable 
        modal
        contentStyle={{ 
          height: 'calc(100% - 45px)',
          overflow: 'hidden', 
        }}
        onHide={() => onHideDetailAvgTransportSpeed()}
      >
        <div style={{ height: '100%',}}>
          <VisibilityWrapper code="avgTransportSpeed-card__roles">
            <TaskAvgTransportSpeedCard
              taskAvgTransportSpeedCardProps={selectedSpeed}
              onUpdate={handleUpdate}
              onDelete={id => {
                AvgTransportSpeedService.deleteAvgTransportSpeed(Number(id)).then(() => loadData());
              }}
              onAdd={handleAdd}
              closeDialog={closeDialog}
              ref={AvgTransportSpeedRef}
            />
          </VisibilityWrapper>
        </div>
      </Dialog>    
      
      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={() => {AvgTransportSpeedService.deleteAvgTransportSpeed(selectedSpeed.id).then(() => loadData())}}
        entityType="среднюю скорость"
        entityName={selectedSpeed?.name}
      />
      
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedSpeed!}
        onSelectionChange={(e) => setSelectedSpeed(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedSpeed(e.data as AvgTransportSpeedUI);
        }}
        metaKeySelection={false}
        scrollable
        paginator
        first={first}
        rows={pageSize}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPage={onPageChanged}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${speeds.length > 0 ? totalCount : 0}`}
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={speeds}
        dataKey="id"
        filterDisplay="row"
        header={header}
        emptyMessage="Средние скорости не найдены."
        onRowReorder={(e) => setSpeeds(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="name"
          filter
          sortable
          field="name"
          header="Название"
          style={{ minWidth: '1rem', width: '25%' }}
          onFilterClear={() =>
            clearFilter('name', filters, setFilters, nameFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию"
              />
            </div>
          }
        />
        <Column
          columnKey="description"
          filter
          sortable
          field="description"
          header="Описание"
          style={{ minWidth: '1rem', width: '25%' }}
          onFilterClear={() =>
            clearFilter('description', filters, setFilters, descriptionFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
        />
        <Column
          columnKey="value"
          filter
          sortable
          field="value"
          header="Средняя скорость (в км/ч)"
          style={{ minWidth: '1rem', width: '25%' }}
          onFilterClear={() =>
            clearFilter('value', filters, setFilters, valueFilterRef)
          }
          body={(rowData) => rowData.value?.toString().replace('.', ',')}
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={valueFilterRef}
                onChange={handleInputChange('value')}
                placeholder="Поиск по средней скорости"
              />
            </div>
          }
        />
        <Column
          columnKey="roadCurvature"
          filter
          sortable
          field="roadCurvature"
          header="Коэффициент кривизны дороги"
          style={{ minWidth: '1rem', width: '25%' }}
          onFilterClear={() =>
            clearFilter('roadCurvature', filters, setFilters, roadCurvatureFilterRef)
          }
          body={(rowData) => rowData.roadCurvature?.toString().replace('.', ',')}
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={roadCurvatureFilterRef}
                onChange={handleInputChange('roadCurvature')}
                placeholder="Поиск по коэффициенту кривизны дороги"
              />
            </div>
          }
        />

      </DataTable> 
    </div>
  );
}
